import type { OnboardingIntegrationIntent } from './onboarding-setup';

export const PROFILE_SETUP_SUBTITLE =
  'Profile is part of My Settings. Review or change the personal details Adulting uses here without hunting through the main sections.';

export const PROFILE_SETUP_BODY =
  'Use this area to review and change the setup details Adulting uses for you. Main sections like Home, Money, Documents, and Calendar are for using the app, not configuring it. If you change profile fields above, save them before leaving this screen.';

export const SETTINGS_SETUP_SUBTITLE =
  'My Settings is where you change setup, integrations, alerts, and account behaviour for Adulting.';

export const SETTINGS_BACK_TO_SETUP_SUBTITLE =
  'Open the Profile section in My Settings to review or change the details Adulting is using.';

export const SETUP_COMPLETE_SUMMARY_TITLE = 'Thanks - your first setup is saved';
export const SETUP_COMPLETE_SUMMARY_BODY =
  'Adulting now has the basics it needs to start feeling useful straight away.';
export const SETUP_COMPLETE_SUMMARY_FOLLOW_UP =
  'Everything you entered lives in My Settings, including Profile and the setup areas, so you can change it later, add more detail, or finish anything you skipped without hunting around.';
export const SETUP_CHOICES_TITLE = 'Saved for later';
export const SETUP_CHOICES_SUBTITLE =
  'Anything set to do later or keep in Adulting stays easy to find from My Settings.';
export const SETUP_NEXT_STEP_TITLE = 'Start here';
export const SETUP_NEXT_STEP_BODY =
  'Life Check is the clearest next step from here. If that is enough for today, stop there. My Settings is where you can review what is saved, change anything, or finish the extras later.';
export const SETUP_NEXT_STEP_PRIMARY_LABEL = 'Start with Life Check';
export const SETUP_NEXT_STEP_SECONDARY_LABEL = 'Open My Settings';
export const SETUP_OPTIONAL_EXTRAS_TITLE = 'You can stop here if you need to';
export const SETUP_OPTIONAL_EXTRAS_BODY =
  'Your core setup is already saved. Any extra setup below will wait in My Settings if today is not the day.';
export const SETUP_FINANCE_SECTION_TITLE = 'Money & finance';
export const SETUP_FINANCE_SECTION_SUBTITLE =
  'Manual finance basics, household bills, and secure bank connection all live together here.';
export const SETUP_FINANCE_HANDOFF_TITLE = 'Finish money setup in My Settings';
export const SETUP_FINANCE_HANDOFF_BODY =
  'When you are ready, open My Settings > Money & finance to choose the setup that suits you: add the basics manually for control, connect banking for speed, or use both together. That keeps everything in one calm place instead of spreading it around the app.';
export const SETUP_FINANCE_HANDOFF_NOTE =
  'Money itself is for statements, budgets, and analysis after the setup path you chose is already in place.';
export const SETUP_ORIENTATION_TITLE = 'Where to find things';
export const SETUP_ORIENTATION_SUBTITLE =
  'You do not need to remember everything at once. These three entry points cover most day-to-day use.';
export const SETUP_ORIENTATION_BRAIN_DUMP =
  'Brain icon = Brain Dump for a quick note or reminder.';
export const SETUP_ORIENTATION_ATTENTION =
  'Bell icon = Things To Do for what needs attention next.';
export const SETUP_ORIENTATION_MORE =
  'More = My Settings and account controls.';
export const DASHBOARD_START_HERE_TITLE = 'Start here';
export const DASHBOARD_START_HERE_BODY =
  'Life Check is the quickest way to give Adulting a clearer starting point. My Settings is where you can review what you already saved.';

export type SetupChoiceSummary = {
  label: string;
  body: string;
};

function describeSetupIntent(intent: OnboardingIntegrationIntent): string {
  if (intent === 'connect-now') {
    return 'You chose to set this up now. You can finish it below or come back from My Settings later.';
  }

  if (intent === 'add-later') {
    return 'You chose to leave this for later. It will stay easy to find in My Settings whenever you are ready.';
  }

  return 'You chose to keep this managed inside Adulting for now. You can still change that later from My Settings.';
}

export function describeSetupChoice(label: string, intent: OnboardingIntegrationIntent): SetupChoiceSummary {
  return {
    label,
    body: describeSetupIntent(intent),
  };
}

export function describeFinanceSetupChoice(intent: OnboardingIntegrationIntent): SetupChoiceSummary {
  if (intent === 'connect-now') {
    return {
      label: 'Banking',
      body: 'You chose to set this up next. Finish the bank connection below if you have the energy, or open My Settings > Money & finance later.',
    };
  }

  if (intent === 'add-later') {
    return {
      label: 'Banking',
      body: 'You chose to leave this for later. It stays easy to find in My Settings > Money & finance.',
    };
  }

  return {
    label: 'Banking',
    body: 'You chose to keep this managed inside Adulting for now. You can still connect a bank later from My Settings > Money & finance.',
  };
}
